import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Search } from "lucide-react";
import api from "@/lib/api";
import CollapsibleCard from "@/components/CollapsibleCard";

const ACTIVE = ["starting", "searching", "crawling"];

/** Google Maps lead finder — zoekt makelaarskantoren per stad via Apify,
 *  haalt e-mails van hun websites en voegt gededupliceerde leads toe. */
export default function LeadFinderPanel({ onLeadsAdded }) {
    const [city, setCity] = useState("");
    const [language, setLanguage] = useState("fr");
    const [audience, setAudience] = useState("agency");
    const [maxPlaces, setMaxPlaces] = useState(30);
    const [starting, setStarting] = useState(false);
    const [runs, setRuns] = useState([]);
    const pollRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/leads/finder/runs", { params: { limit: 3 } });
            setRuns(data.runs || []);
            return data.runs || [];
        } catch { return []; }
    }, []);

    useEffect(() => { load(); }, [load]);

    const active = runs.find((r) => ACTIVE.includes(r.status));

    useEffect(() => {
        if (!active) { clearInterval(pollRef.current); return; }
        pollRef.current = setInterval(async () => {
            const rs = await load();
            const still = rs.find((r) => ACTIVE.includes(r.status));
            if (!still) {
                clearInterval(pollRef.current);
                const last = rs[0];
                if (last?.status === "done") {
                    toast.success(`Klaar: ${last.leads_created} nieuwe leads (${last.duplicates_skipped} dubbels overgeslagen, ${last.no_email} zonder e-mail)`);
                    onLeadsAdded?.();
                } else if (last?.status === "failed") {
                    toast.error(`Zoeken mislukt: ${last.error || "onbekende fout"}`);
                }
            }
        }, 6000);
        return () => clearInterval(pollRef.current);
    }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const start = async () => {
        if (!city.trim()) { toast.error("Vul een stad in (bv. Liège, Namur, Bruxelles)"); return; }
        setStarting(true);
        try {
            await api.post("/admin/leads/finder/start", { city: city.trim(), language, max_places: maxPlaces, audience });
            toast.success("Zoekopdracht gestart — dit duurt 3-8 minuten. Nieuwe leads verschijnen automatisch in je lijst.");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Starten mislukt");
        }
        setStarting(false);
    };

    const last = runs[0];

    return (
        <div className="mb-6" data-testid="lead-finder-panel">
            <CollapsibleCard
                storageKey="vsf_leadfinder_open"
                testId="lead-finder-collapse"
                title="Leads zoeken via Google Maps"
                icon={<MapPin className="h-4 w-4 text-[#E5A93D] shrink-0" />}
                extra={active ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-[#9A6E1B] font-bold">
                        <Loader2 className="h-3 w-3 animate-spin" /> bezig
                    </span>
                ) : null}
            >
            <p className="text-xs text-zinc-500 mb-3 max-w-2xl">
                Zoekt makelaarskantoren óf vastgoedfotografen per stad, haalt automatisch het e-mailadres van hun website en voegt ze toe
                als lead — <strong>dubbele e-mailadressen en bestaande leads worden overgeslagen</strong>. De taal bepaalt in welke taal ze gemaild worden; fotografen krijgen hun eigen mailreeks (bulk-retouche pitch).
            </p>
            <div className="flex flex-wrap items-center gap-2">
                <select
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    disabled={!!active}
                    data-testid="lead-finder-audience-select"
                    className="h-10 px-2 rounded-sm border border-zinc-200 bg-white text-sm text-zinc-800 font-medium"
                >
                    <option value="agency">🏠 Makelaars</option>
                    <option value="photographer">📸 Vastgoedfotografen</option>
                </select>
                <input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Stad (bv. Liège, Namur, Bruxelles)"
                    disabled={!!active}
                    data-testid="lead-finder-city-input"
                    className="h-10 px-3 rounded-sm border border-zinc-200 bg-white text-sm text-zinc-900 w-64"
                />
                <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    disabled={!!active}
                    data-testid="lead-finder-language-select"
                    className="h-10 px-2 rounded-sm border border-zinc-200 bg-white text-sm text-zinc-800"
                >
                    <option value="fr">Franstalig (FR mails)</option>
                    <option value="en">Engelstalig (EN mails)</option>
                    <option value="nl">Nederlandstalig (NL mails)</option>
                    <option value="es">Spaanstalig (ES mails)</option>
                </select>
                <select
                    value={maxPlaces}
                    onChange={(e) => setMaxPlaces(Number(e.target.value))}
                    disabled={!!active}
                    data-testid="lead-finder-max-select"
                    className="h-10 px-2 rounded-sm border border-zinc-200 bg-white text-sm text-zinc-800"
                >
                    <option value={20}>20 kantoren</option>
                    <option value={30}>30 kantoren</option>
                    <option value={45}>45 kantoren</option>
                    <option value={60}>60 kantoren</option>
                </select>
                <button
                    type="button"
                    onClick={start}
                    disabled={starting || !!active}
                    data-testid="lead-finder-start-btn"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm transition active:scale-[0.98] disabled:opacity-50"
                >
                    {starting || active ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    {active ? "Bezig…" : "Zoek leads"}
                </button>
            </div>
            {active && (
                <div className="mt-3 p-2.5 rounded-sm border border-[#E5A93D]/30 bg-[#E5A93D]/[0.06] text-xs text-zinc-700 flex items-center gap-2" data-testid="lead-finder-active">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-[#E5A93D]" />
                    <span>
                        <strong>{active.query}</strong> — status: {active.status === "searching" ? "Google Maps doorzoeken…" : active.status === "crawling" ? `websites checken op e-mails… (${active.leads_created} leads gevonden)` : "starten…"}
                    </span>
                </div>
            )}
            {!active && last && last.status === "done" && (
                <p className="mt-2 text-[11px] text-zinc-400" data-testid="lead-finder-last-result">
                    Laatste zoekopdracht "{last.query}": <strong className="text-emerald-600">{last.leads_created} nieuw</strong> · {last.duplicates_skipped} dubbel · {last.no_email} zonder e-mail
                </p>
            )}
            </CollapsibleCard>
        </div>
    );
}
